import { create } from "zustand";
import highlightsService from "../api/highlights";
import { DEFAULT_HIGHLIGHT_COLOR } from "../utils/constants";
import { useAuthStore } from "./authStore";

// Helper to get current user ID
const getCurrentUserId = () => {
  const authState = useAuthStore.getState();
  return authState.currentUser?.id;
};

// Local storage helpers for selectedColor only
const STORAGE_KEY = "highlight-color-storage";

const loadColorFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.selectedColor || DEFAULT_HIGHLIGHT_COLOR;
    }
    return DEFAULT_HIGHLIGHT_COLOR;
  } catch (error) {
    console.warn("Failed to load highlight color from storage:", error);
    return DEFAULT_HIGHLIGHT_COLOR;
  }
};

const saveColorToStorage = (selectedColor) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ selectedColor })
    );
  } catch (error) {
    console.warn("Failed to save highlight color to storage:", error);
  }
};

/**
 * Zustand store for managing text highlighting functionality
 * Handles highlight state, color selection, and highlight persistence via API
 */
export const useHighlightStore = create((set, get) => {
  return {
    // Current selected highlight color
    selectedColor: loadColorFromStorage(),

    // Map of highlights by topic/page ID
    // Structure: { topicId: { pageId: [highlights] } }
    // Each highlight object here will have its `id` as the backend ID,
    // and `highlightData.id` as the frontend (web-highlighter) ID.
    highlights: {},

    // Currently active highlighter instance
    highlighter: null,

    // Loading state for highlight operations
    isLoading: false,

    // Error state
    error: null,

    /**
     * Set the selected highlight color
     * @param {Object} color - Color object from HIGHLIGHT_COLORS
     */
    setSelectedColor: (color) => {
      // Update the selected color in the store
      set({ selectedColor: color });

      // Save to localStorage
      saveColorToStorage(color);

      // Log the color change (existing highlights should persist)
      console.log(
        "✅ Selected color updated to:",
        color.name,
        "- Existing highlights will persist"
      );
    },

    /**
     * Set the highlighter instance
     * @param {Object} highlighter - Web-highlighter instance
     */
    setHighlighter: (highlighter) => {
      set({ highlighter });
    },

    /**
     * Add a new highlight
     * @param {string} topicId - Topic identifier
     * @param {string} pageId - Page identifier (resourceId)
     * @param {Object} highlightPayload - Highlight data from web-highlighter (contains frontend ID as 'id')
     *                                   e.g., { id: 'frontend-id', startMeta: ..., endMeta: ..., colorId: ... }
     * @param {string} resourceId - Resource ID from the topic
     * @param {string} courseId - Course ID from the topic
     */
    addHighlight: async (topicId, pageId, highlightPayload, resourceId = null, courseId = null) => {
      const userId = getCurrentUserId();
      if (!userId) {
        console.error('User not authenticated');
        set({ error: 'User not authenticated' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        console.log(
          `💾 Adding highlight to topicId="${topicId}", pageId="${pageId}" with frontend ID: ${highlightPayload.id}`,
          { resourceId, courseId }
        );

        // Validate required resourceId and courseId
        if (!resourceId || !courseId) {
          console.warn('resourceId or courseId missing in addHighlight - attempting to extract from highlight payload');
          resourceId = resourceId || highlightPayload.resourceId || pageId;
          courseId = courseId || highlightPayload.courseId;

          if (!resourceId || !courseId) {
            throw new Error('resourceId and courseId are required for creating highlights');
          }
        }

        // Prepare highlight data for API. The highlightPayload's `id` is the frontend ID.
        const highlightDataForApi = {
          ...highlightPayload,
          pageId,
          topicId,
          resourceId,
          courseId
        };

        // API call to create highlight. This returns the full API response object.
        const apiResponse = await highlightsService.createHighlight({
          userId,
          topicId,
          resourceId,
          courseId,
          highlight: highlightDataForApi, // This 'highlight' object has frontend ID as its 'id'
          title: highlightPayload.comment?.text?.substring(0, 50) || 'Untitled Highlight',
          tags: ''
        });

        // Extract the actual highlight data object from the API response
        const createdHighlightData = apiResponse.data;

        // Normalize the highlight object to be stored in Zustand.
        // The top-level `id` will be the backend ID.
        // The `highlightData` property will contain the parsed JSON, including the frontend ID.
        let finalHighlightToStore = { ...createdHighlightData }; // Start with the actual data

        if (finalHighlightToStore.highlightData && typeof finalHighlightToStore.highlightData === 'string') {
          try {
            const parsedData = JSON.parse(finalHighlightToStore.highlightData);
            finalHighlightToStore = {
              ...finalHighlightToStore, // Keep all properties from API data
              highlightData: parsedData, // Store parsed object for web-highlighter's deserialization
              // Extract properties needed by web-highlighter deserializer for convenience
              startMeta: parsedData.startMeta,
              endMeta: parsedData.endMeta,
              colorId: parsedData.colorId,
              pageId: parsedData.pageId || finalHighlightToStore.pageId || finalHighlightToStore.resourceId, // Ensure pageId is consistent
              topicId: parsedData.topicId || finalHighlightToStore.topicId, // Ensure topicId is consistent
              resourceId: parsedData.resourceId || finalHighlightToStore.resourceId, // Ensure resourceId is consistent
              courseId: parsedData.courseId || finalHighlightToStore.courseId, // Ensure courseId is consistent
              createdAt: parsedData.createdAt || finalHighlightToStore.createdAt
            };
          } catch (error) {
            console.warn('Failed to parse highlightData string from API response:', error, finalHighlightToStore);
            // Fallback: if parsing fails, ensure `highlightData` is still an object to prevent later errors
            finalHighlightToStore.highlightData = {};
          }
        } else if (!finalHighlightToStore.highlightData) {
          // Ensure highlightData is at least an empty object if not present or parsed
          finalHighlightToStore.highlightData = {};
        }

        // Update local state
        const state = get();
        const highlights = state.highlights || {};
        const existingPageHighlights = highlights[topicId]?.[pageId] || [];

        // When adding a new highlight, remove any locally existing highlights with the same
        // frontend ID (web-highlighter's ID). This prevents duplicates if web-highlighter
        // triggers creation for an already (locally) existing but unsaved highlight,
        // or if our internal logic needs to replace.
        const updatedPageHighlights = existingPageHighlights.filter(h =>
          h.highlightData?.id !== finalHighlightToStore.highlightData?.id
        );

        updatedPageHighlights.push(finalHighlightToStore);

        const updatedHighlights = {
          ...highlights,
          [topicId]: {
            ...(highlights[topicId] || {}),
            [pageId]: updatedPageHighlights
          },
        };

        set({ highlights: updatedHighlights, isLoading: false });

        console.log(`✅ Highlight added successfully. Backend ID: ${finalHighlightToStore.id}, Frontend ID: ${finalHighlightToStore.highlightData?.id}`);
        return finalHighlightToStore;
      } catch (error) {
        console.error('Error adding highlight:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    },

    /**
     * Delete a highlight by its frontend-generated ID (from web-highlighter)
     * @param {string} frontendHighlightId - Highlight identifier (the ID web-highlighter uses)
     */
    delete: async (frontendHighlightId) => {
      set({ isLoading: true, error: null });

      try {
        const state = get();
        const highlights = state.highlights || {};
        let backendHighlightIdToDelete = null;
        let topicIdFound = null;
        let pageIdFound = null;

        // Iterate through all stored highlights to find the backend ID
        // corresponding to the given frontendHighlightId.
        // We're iterating here because the frontend ID might not directly map to a specific topic/page in the store
        // without a reverse lookup structure.
        Object.keys(highlights).some(tId => { // Using some() to allow early exit
          return Object.keys(highlights[tId] || {}).some(pId => {
            const pageHighlights = highlights[tId][pId] || [];
            const foundHighlight = pageHighlights.find(h =>
              h.highlightData?.id === frontendHighlightId // Match by frontend ID inside highlightData
            );
            if (foundHighlight) {
              backendHighlightIdToDelete = foundHighlight.id; // This is the backend ID
              topicIdFound = tId;
              pageIdFound = pId;
              return true; // Exit inner loop
            }
            return false;
          });
        });

        if (!backendHighlightIdToDelete) {
          console.warn(`Highlight with frontend ID ${frontendHighlightId} not found in store, cannot delete.`);
          set({ isLoading: false, error: 'Highlight not found for deletion.' });
          return;
        }

        // Delete from API using the backend ID
        await highlightsService.deleteHighlight(backendHighlightIdToDelete);

        // Update local state: filter out the highlight using its backend ID
        const updatedHighlights = { ...highlights };
        if (topicIdFound && pageIdFound && updatedHighlights[topicIdFound] && updatedHighlights[topicIdFound][pageIdFound]) {
          updatedHighlights[topicIdFound][pageIdFound] = updatedHighlights[topicIdFound][pageIdFound].filter(
            (h) => h.id !== backendHighlightIdToDelete // Filter by backend ID
          );
        }

        set({ highlights: updatedHighlights, isLoading: false });
        console.log(`✅ Highlight deleted successfully. Frontend ID: ${frontendHighlightId}, Backend ID: ${backendHighlightIdToDelete}`);

      } catch (error) {
        console.error('Error deleting highlight:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    },

    /**
     * Fetch highlights from API for a specific topic and page
     * @param {string} topicId - Topic identifier
     * @param {string} pageId - Page identifier (for local filtering)
     */
    fetchHighlights: async (topicId, pageId) => {
      const userId = getCurrentUserId();
      if (!userId) {
        console.error('User not authenticated');
        set({ error: 'User not authenticated' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        console.log(`🔄 Fetching highlights for topicId="${topicId}"`);

        // The highlightsService.getHighlights should return an array of highlight data objects directly
        const allTopicHighlights = await highlightsService.getHighlights(userId, topicId);

        // Parse highlightData JSON strings and filter highlights by pageId locally.
        // Ensure the stored highlight objects are consistently structured.
        const pageHighlights = allTopicHighlights.filter(highlight => {
          // Prioritize top-level pageId or resourceId, then inside highlightData
          return highlight.pageId === pageId ||
            highlight.resourceId === pageId ||
            (highlight.highlightData && typeof highlight.highlightData === 'object' && highlight.highlightData.pageId === pageId) ||
            (typeof highlight.highlightData === 'string' && JSON.parse(highlight.highlightData || '{}').pageId === pageId);
        }).map(highlight => {
          let parsedData = null;
          if (highlight.highlightData && typeof highlight.highlightData === 'string') {
            try {
              parsedData = JSON.parse(highlight.highlightData);
            } catch (error) {
              console.warn('Failed to parse highlightData string from fetched highlight:', highlight.id, error);
            }
          } else if (highlight.highlightData && typeof highlight.highlightData === 'object') {
            parsedData = highlight.highlightData;
          }

          // Return the normalized highlight object for the store
          return {
            ...highlight, // Keep all properties from API response, including backend ID
            id: highlight.id, // Ensure top-level 'id' is the backend ID
            highlightData: parsedData, // Store parsed object
            // Extract commonly used web-highlighter properties for easier access/deserialization
            startMeta: parsedData?.startMeta,
            endMeta: parsedData?.endMeta,
            colorId: parsedData?.colorId,
            pageId: parsedData?.pageId || highlight.pageId || highlight.resourceId, // Prioritize parsed, then top-level
            topicId: parsedData?.topicId || highlight.topicId,
            resourceId: parsedData?.resourceId || highlight.resourceId, // Ensure resourceId is preserved
            courseId: parsedData?.courseId || highlight.courseId, // Ensure courseId is preserved
            createdAt: parsedData?.createdAt || highlight.createdAt
          };
        });

        // Update local state
        const state = get();
        const highlights = state.highlights || {};

        const updatedHighlights = {
          ...highlights,
          [topicId]: {
            ...(highlights[topicId] || {}),
            [pageId]: pageHighlights
          },
        };

        set({ highlights: updatedHighlights, isLoading: false });

        console.log(`✅ Fetched ${pageHighlights.length} highlights for page "${pageId}" from ${allTopicHighlights.length} total highlights for topic "${topicId}"`);
        return pageHighlights;
      } catch (error) {
        console.error('Error fetching highlights:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    },

    /**
     * Fetch all highlights for a specific course
     * @param {string} courseId - Course identifier
     * @returns {Promise<Array>} Array of all highlights for the course
     */
    fetchHighlightsByCourse: async (courseId) => {
      const userId = getCurrentUserId();
      if (!userId) {
        console.error('User not authenticated');
        set({ error: 'User not authenticated' });
        return [];
      }

      set({ isLoading: true, error: null });

      try {
        console.log(`🔄 Fetching highlights for courseId="${courseId}"`);

        // Use the new getHighlightsByCourse method from highlightsService
        const allCourseHighlights = await highlightsService.getHighlightsByCourse(userId, courseId);

        // Parse and normalize the highlights
        const normalizedHighlights = allCourseHighlights.map(highlight => {
          let parsedData = null;
          if (highlight.highlightData && typeof highlight.highlightData === 'string') {
            try {
              parsedData = JSON.parse(highlight.highlightData);
            } catch (error) {
              console.warn('Failed to parse highlightData string from fetched highlight:', highlight.id, error);
            }
          } else if (highlight.highlightData && typeof highlight.highlightData === 'object') {
            parsedData = highlight.highlightData;
          }

          // Return the normalized highlight object
          return {
            ...highlight,
            id: highlight.id,
            highlightData: parsedData,
            startMeta: parsedData?.startMeta,
            endMeta: parsedData?.endMeta,
            colorId: parsedData?.colorId,
            pageId: parsedData?.pageId || highlight.pageId || highlight.resourceId,
            topicId: parsedData?.topicId || highlight.topicId,
            resourceId: parsedData?.resourceId || highlight.resourceId,
            courseId: parsedData?.courseId || highlight.courseId || courseId,
            createdAt: parsedData?.createdAt || highlight.createdAt
          };
        });

        // Update local state by organizing highlights by topicId and pageId
        const state = get();
        const highlights = state.highlights || {};
        const updatedHighlights = { ...highlights };

        // Group highlights by topicId and pageId
        normalizedHighlights.forEach(highlight => {
          const { topicId, pageId } = highlight;
          if (topicId && pageId) {
            if (!updatedHighlights[topicId]) {
              updatedHighlights[topicId] = {};
            }
            if (!updatedHighlights[topicId][pageId]) {
              updatedHighlights[topicId][pageId] = [];
            }
            // Check if highlight already exists to avoid duplicates
            const existingIndex = updatedHighlights[topicId][pageId].findIndex(h => h.id === highlight.id);
            if (existingIndex >= 0) {
              updatedHighlights[topicId][pageId][existingIndex] = highlight;
            } else {
              updatedHighlights[topicId][pageId].push(highlight);
            }
          }
        });

        set({ highlights: updatedHighlights, isLoading: false });

        console.log(`✅ Fetched ${normalizedHighlights.length} highlights for course "${courseId}"`);
        return normalizedHighlights;
      } catch (error) {
        console.error('Error fetching highlights by course:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    },

    /**
     * Get highlights for a specific topic and page from local state
     * @param {string} topicId - Topic identifier
     * @param {string} pageId - Page identifier
     * @returns {Array} Array of highlights (top-level ID is backend ID)
     */
    getHighlights: (topicId, pageId) => {
      const { highlights } = get();
      return highlights[topicId]?.[pageId] || [];
    },

    /**
     * Update an existing highlight
     * @param {string} highlightId - Backend highlight identifier
     * @param {Object} updates - Updates to apply (e.g., new color, content)
     */
    updateHighlight: async (highlightId, updates) => {
      set({ isLoading: true, error: null });

      try {
        // Retrieve current highlight to merge updates
        let currentHighlight = null;
        const state = get();
        const highlights = state.highlights || {};
        let topicIdFound = null;
        let pageIdFound = null;

        Object.keys(highlights).some(tId => {
          return Object.keys(highlights[tId] || {}).some(pId => {
            const pageHighlights = highlights[tId][pId] || [];
            const found = pageHighlights.find(h => h.id === highlightId);
            if (found) {
              currentHighlight = found;
              topicIdFound = tId;
              pageIdFound = pId;
              return true;
            }
            return false;
          });
        });

        if (!currentHighlight) {
          console.warn(`Highlight with backend ID ${highlightId} not found for update.`);
          set({ isLoading: false, error: 'Highlight not found for update.' });
          return null;
        }

        // Merge updates, particularly for highlightData
        let updatedHighlightData = currentHighlight.highlightData;
        if (updates.colorId) {
          updatedHighlightData = { ...updatedHighlightData, colorId: updates.colorId };
        }
        // Add other highlightData updates here if needed, e.g., if (updates.comment) updatedHighlightData.comment = updates.comment;

        // Prepare payload for API
        const apiUpdates = {
          ...updates,
          highlightData: JSON.stringify(updatedHighlightData), // Send stringified for API
        };

        // Update via API. This returns the full API response object.
        const apiResponse = await highlightsService.updateHighlight(highlightId, apiUpdates);
        const apiUpdatedHighlightData = apiResponse.data; // Extract the actual highlight data

        // Normalize the updated highlight object for Zustand store
        let finalUpdatedHighlight = { ...apiUpdatedHighlightData };

        if (finalUpdatedHighlight.highlightData && typeof finalUpdatedHighlight.highlightData === 'string') {
          try {
            const parsedData = JSON.parse(finalUpdatedHighlight.highlightData);
            finalUpdatedHighlight = {
              ...finalUpdatedHighlight,
              highlightData: parsedData,
              id: finalUpdatedHighlight.id, // Backend ID
              startMeta: parsedData.startMeta,
              endMeta: parsedData.endMeta,
              colorId: parsedData.colorId,
              pageId: parsedData.pageId || finalUpdatedHighlight.pageId || finalUpdatedHighlight.resourceId,
              topicId: parsedData.topicId || finalUpdatedHighlight.topicId,
              resourceId: parsedData.resourceId || finalUpdatedHighlight.resourceId, // Ensure resourceId is preserved
              courseId: parsedData.courseId || finalUpdatedHighlight.courseId, // Ensure courseId is preserved
              createdAt: parsedData.createdAt || finalUpdatedHighlight.createdAt
            };
          } catch (error) {
            console.warn('Failed to parse highlightData string from API response during update:', error, finalUpdatedHighlight);
            finalUpdatedHighlight.highlightData = {};
          }
        } else if (!finalUpdatedHighlight.highlightData) {
          finalUpdatedHighlight.highlightData = {};
        }

        // Update local state
        const updatedHighlights = { ...highlights };
        if (topicIdFound && pageIdFound && updatedHighlights[topicIdFound] && updatedHighlights[topicIdFound][pageIdFound]) {
          const pageHighlights = updatedHighlights[topicIdFound][pageIdFound] || [];
          const highlightIndex = pageHighlights.findIndex(h => h.id === highlightId);

          if (highlightIndex !== -1) {
            // Replace the old highlight object with the new normalized one
            updatedHighlights[topicIdFound][pageIdFound] = [
              ...pageHighlights.slice(0, highlightIndex),
              finalUpdatedHighlight,
              ...pageHighlights.slice(highlightIndex + 1)
            ];
          }
        }

        set({ highlights: updatedHighlights, isLoading: false });
        console.log(`✅ Highlight updated successfully. Backend ID: ${finalUpdatedHighlight.id}, Frontend ID: ${finalUpdatedHighlight.highlightData?.id}`);
        return finalUpdatedHighlight;
      } catch (error) {
        console.error('Error updating highlight:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    },

    /**
     * Clear all highlights for a topic/page (local state only)
     * @param {string} topicId - Topic identifier
     * @param {string} pageId - Page identifier (optional)
     */
    clearHighlights: (topicId, pageId = null) => {
      const { highlights } = get();

      if (highlights[topicId]) {
        if (pageId) {
          // Clear specific page
          if (highlights[topicId][pageId]) {
            set({
              highlights: {
                ...highlights,
                [topicId]: {
                  ...highlights[topicId],
                  [pageId]: [],
                },
              },
            });
          }
        } else {
          // Clear entire topic
          set({
            highlights: {
              ...highlights,
              [topicId]: {},
            },
          });
        }
      }
      console.log(`🧹 Cleared highlights for topicId: ${topicId}${pageId ? `, pageId: ${pageId}` : ''}`);
    },

    /**
     * Set loading state
     * @param {boolean} loading - Loading state
     */
    setLoading: (loading) => {
      set({ isLoading: loading });
    },

    /**
     * Set error state
     * @param {string|null} error - Error message or null
     */
    setError: (error) => {
      set({ error });
    },

    /**
     * Clear error state
     */
    clearError: () => {
      set({ error: null });
    },

    /**
     * Reset store to initial state
     */
    reset: () => {
      set({
        selectedColor: DEFAULT_HIGHLIGHT_COLOR,
        highlights: {},
        highlighter: null,
        isLoading: false,
        error: null,
      });
    },
  };
});